import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { Employee } from '../model/employee.model';
import { BankAmount } from '../model/bank.model';
import { CustomerBills } from '../model/customerbills.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { exportBillingDashboardToPDF } from './billing-dashboard-pdf';

export interface BillingSummary {
  totalCustomers: number;
  totalBilledCustomers: number;
  totalBillAmount: number;
  totalPaid: number;
  totalRh: number;
  totalRc: number;
  totalBankAmount: number;
  totalFarqi: number;
  totalBadDebt: number;
  totalDiscount: number;
  totalVat: number;
  totalWadarta: number;
  totalMerchantDebit: number;
  totalMerchantCredit: number;
  totalAccountSent: number;
  totalAccountReceived: number;
  totalUnitsConsumed: number;
}

export interface EmployeeBillingSummary extends BillingSummary {
  employeeId: number;
  employeeName: string;
}

export interface ColumnConfig {
  key: keyof EmployeeBillingSummary;
  label: string;
  visible: boolean;
  isMetric?: boolean;
}

export interface SummaryFieldConfig {
  key: keyof BillingSummary | 'totalEmployees';
  label: string;
  type: 'count' | 'currency';
  visible: boolean;
}

@Component({
  selector: 'app-billing-dashboard',
  templateUrl: './billing-dashboard.component.html',
  styleUrls: ['./billing-dashboard.component.scss']
})
export class BillingDashboardComponent implements OnInit {
  /** true when routed as /billing-branch-dashboard — same report grouped by
   *  customer branch instead of collector. Driven by route data.groupBy. */
  groupByBranch = false;

  billingSummary: BillingSummary = {
    totalCustomers: 0,
    totalBilledCustomers: 0,
    totalBillAmount: 0,
    totalPaid: 0,
    totalRh: 0,
    totalRc: 0,
    totalBankAmount: 0,
    totalFarqi: 0,
    totalBadDebt: 0,
    totalDiscount: 0,
    totalVat: 0,
    totalWadarta: 0,
    totalMerchantDebit: 0,
    totalMerchantCredit: 0,
    totalAccountSent: 0,
    totalAccountReceived: 0,
    totalUnitsConsumed: 0
  };

  employeeSummaries: EmployeeBillingSummary[] = [];
  filteredEmployeeSummaries: EmployeeBillingSummary[] = [];
  loading = false;
  currentCycleStart: string = '';
  currentCycleEnd: string = '';
  searchTerm: string = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  excludeDanGuud = false; // false = Dan Guud customers; true = non-Dan Guud customers

  // Employee multi-select filter (empty set = show all)
  selectedEmployeeIds = new Set<number>();
  showEmployeeFilter = false;

  // Branch multi-select filter (empty set = show all). Only for unrestricted
  // users (supersysadmin or employees with no branch_name); restricted users
  // are already scoped server-side.
  availableBranches: string[] = [];
  selectedBranches = new Set<string>();
  showBranchFilter = false;
  canPickBranches = false;

  // Raw response cached so branch changes re-filter without a new API call
  private rawCollectors: any[] = [];
  private rawMerchantSums: any = {};
  private rawAccountSums: any = {};

  // Branch mode: bank sums re-keyed by branch index instead of employeeId
  private bankAmountOverride: Map<number, number> | null = null;

  // Drill-down: customer-level bills for the clicked summary row
  private billsByRowKey = new Map<number, CustomerBills[]>();
  selectedDrillSummary: EmployeeBillingSummary | null = null;
  drillBills: CustomerBills[] = [];

  // Top-N filter: 0 = show all rows; otherwise keep the N best rows by topNMetric
  topN = 0;
  topNMetric: keyof EmployeeBillingSummary = 'totalBillAmount';
  topNMetricOptions: { key: keyof EmployeeBillingSummary; label: string }[] = [
    { key: 'totalBillAmount', label: 'Bill Amount' },
    { key: 'totalPaid', label: 'Paid' },
    { key: 'totalCustomers', label: 'Customers' },
    { key: 'totalUnitsConsumed', label: 'Units Consumed' },
    { key: 'totalRc', label: 'RC' }
  ];

  // Date cycle search properties
  availableCycles: { label: string; startDate: string; endDate: string }[] = [];
  selectedCycleControl = new FormControl('');
  showCycleSelector: boolean = false;

  // Simple cache to prevent multiple employee API calls
  private employeesDataLoaded: boolean = false;
  
  // Caching properties
  employeesCache: any[] = [];
  employeesLoaded: boolean = false;
  bankAmountsCache: BankAmount[] = [];
  bankAmountsLoaded: boolean = false;

  // Column selector
  showColumnSelector = false;
  allColumns: ColumnConfig[] = [
    { key: 'employeeId', label: 'Emp ID', visible: true },
    { key: 'employeeName', label: 'Employee', visible: true },
    { key: 'totalCustomers', label: 'Customers', visible: true },
    { key: 'totalBilledCustomers', label: 'Billed Customers', visible: true },
    { key: 'totalUnitsConsumed', label: 'Farqi M3', visible: true },
    { key: 'totalBillAmount', label: 'Bill Amount', visible: true },
    { key: 'totalPaid', label: 'Paid', visible: true },
    { key: 'totalRh', label: 'RH', visible: true },
    { key: 'totalRc', label: 'RC', visible: true },
    { key: 'totalBankAmount', label: 'Other Bank Amount', visible: true },
    { key: 'totalFarqi', label: 'Farqi', visible: true },
    { key: 'totalWadarta', label: 'Wadarta', visible: true },
    { key: 'totalBadDebt', label: 'Daan Guud', visible: true },
    { key: 'totalDiscount', label: 'Discount', visible: true },
    { key: 'totalVat', label: 'VAT', visible: true },
    { key: 'totalMerchantDebit', label: 'Merchant Debit', visible: true },
    { key: 'totalMerchantCredit', label: 'Merchant Credit', visible: true },
    { key: 'totalAccountSent', label: 'Account Sent', visible: true },
    { key: 'totalAccountReceived', label: 'Account Received', visible: true }
  ];

  // PDF summary card selector (top summary section of the exported PDF)
  showSummarySelector = false;
  summaryFields: SummaryFieldConfig[] = [
    { key: 'totalCustomers', label: 'Total Customers', type: 'count', visible: true },
    { key: 'totalBilledCustomers', label: 'Billed Customers', type: 'count', visible: true },
    { key: 'totalEmployees', label: 'Total Employees', type: 'count', visible: true },
    { key: 'totalUnitsConsumed', label: 'Total Units Consumed', type: 'count', visible: true },
    { key: 'totalBillAmount', label: 'Grand Total Bill', type: 'currency', visible: true },
    { key: 'totalPaid', label: 'Total Paid', type: 'currency', visible: true },
    { key: 'totalRh', label: 'Total RH', type: 'currency', visible: true },
    { key: 'totalRc', label: 'Total RC', type: 'currency', visible: true },
    { key: 'totalBadDebt', label: 'Total Daan Guud', type: 'currency', visible: true },
    { key: 'totalDiscount', label: 'Total Discount', type: 'currency', visible: true },
    { key: 'totalVat', label: 'Total VAT', type: 'currency', visible: true },
    { key: 'totalWadarta', label: 'Total Wadarta', type: 'currency', visible: true },
    { key: 'totalBankAmount', label: 'Total Bank Amount', type: 'currency', visible: true },
    { key: 'totalFarqi', label: 'Total Farqi', type: 'currency', visible: true },
    { key: 'totalMerchantDebit', label: 'Merchant Debit', type: 'currency', visible: true },
    { key: 'totalMerchantCredit', label: 'Merchant Credit', type: 'currency', visible: true },
    { key: 'totalAccountSent', label: 'Account Sent', type: 'currency', visible: true },
    { key: 'totalAccountReceived', label: 'Account Received', type: 'currency', visible: true }
  ];

  constructor(
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute
  ) {
    this.groupByBranch = this.route.snapshot.data?.['groupBy'] === 'branch';
    if (this.groupByBranch) {
      // Relabel the identity columns/summary for the branch variant
      this.allColumns[0].label = '#';
      this.allColumns[1].label = 'Branch';
      const empField = this.summaryFields.find(f => f.key === 'totalEmployees');
      if (empField) {
        empField.label = 'Total Branches';
      }
    }
  }

  ngOnInit(): void {
    console.log('BillingDashboardComponent ngOnInit called'); // Debug log
    this.initializeAvailableCycles();
    const isSuperSysAdmin = localStorage.getItem('isSuperSysAdmin') === 'true';
    const userBranch = (localStorage.getItem('userBranch') || '').trim();
    // Branch picker only makes sense when the caller can see more than one branch
    this.canPickBranches = isSuperSysAdmin || !userBranch;
    if (this.canPickBranches) {
      this.loadBranches();
    }
    this.loadAllDataParallel(); // Load all data in parallel (includes employees on first load)
  }

  private loadBranches(): void {
    this.apiService.getBranches().subscribe({
      next: (res: any) => { this.availableBranches = res?.branches || []; },
      error: () => { this.availableBranches = []; }
    });
  }

  toggleBranchFilter(): void {
    this.showBranchFilter = !this.showBranchFilter;
  }

  isBranchSelected(branch: string): boolean {
    return this.selectedBranches.size === 0 || this.selectedBranches.has(branch);
  }

  toggleBranchSelection(branch: string): void {
    // From implicit 'all' (empty set), seed with every branch first so that
    // unchecking one leaves the others selected
    if (this.selectedBranches.size === 0) {
      this.availableBranches.forEach(b => this.selectedBranches.add(b));
    }
    if (this.selectedBranches.has(branch)) {
      this.selectedBranches.delete(branch);
    } else {
      this.selectedBranches.add(branch);
    }
    this.rebuildDashboard();
  }

  get isAllBranchesSelected(): boolean {
    return this.selectedBranches.size === 0 ||
        (this.availableBranches.length > 0 &&
         this.selectedBranches.size === this.availableBranches.length);
  }

  toggleSelectAllBranches(): void {
    if (this.isAllBranchesSelected && this.selectedBranches.size > 0) {
      this.selectedBranches.clear();
    } else {
      this.availableBranches.forEach(b => this.selectedBranches.add(b));
    }
    this.rebuildDashboard();
  }

  get branchFilterLabel(): string {
    if (this.isAllBranchesSelected) {
      return 'All Branches';
    }
    return this.selectedBranches.size + ' selected';
  }

  private isBranchFiltered(): boolean {
    return this.canPickBranches && this.selectedBranches.size > 0 &&
        !this.isAllBranchesSelected;
  }

  /** Rebuild summaries from the cached payload with the branch filter applied. */
  private rebuildDashboard(): void {
    this.bankAmountOverride = null;
    const branchActive = this.isBranchFiltered();
    const scopedCollectors = branchActive
      ? this.rawCollectors.map((c: any) => ({
          ...c,
          customerBills: (c.customerBills || []).filter((b: CustomerBills) =>
            this.selectedBranches.has((b.branchName || '').trim()))
        }))
      : this.rawCollectors;

    // Cache employees (collectors) for name lookups / PDF / dropdowns
    this.employeesCache = scopedCollectors.map((c: any) => ({
      employeeId: c.employeeId,
      employeeName: c.employeeName,
      roles: [{ roleName: 'collector' }]
    }));
    this.employeesLoaded = true;

    const employeeMap = new Map<number, CustomerBills[]>();
    const allBills: CustomerBills[] = [];
    const employeesWithData: Employee[] = [];

    scopedCollectors.forEach((c: any) => {
      const bills: CustomerBills[] = c.customerBills || [];
      const hasBillCreated = bills.some(b => b.billDate && b.billDate !== null);
      const hasRHAmount = bills.some(b => b.prevMonthOutstandingBal !== null && b.prevMonthOutstandingBal !== 0);

      if (hasBillCreated || hasRHAmount) {
        employeeMap.set(c.employeeId, bills);
        allBills.push(...bills);
        employeesWithData.push({
          employeeId: c.employeeId,
          employeeName: c.employeeName,
          roles: [{ roleName: 'collector' }]
        } as Employee);
      }
    });

    if (this.groupByBranch) {
      this.rebuildAsBranches(scopedCollectors);
      return;
    }

    this.processViewBillInfoData(employeeMap, allBills, employeesWithData, this.rawMerchantSums, this.rawAccountSums);
  }

  /**
   * Branch variant of rebuildDashboard: identical math to the employee view but
   * grouped by customers.branch_name. Each branch becomes a pseudo-"employee"
   * row so all existing aggregation, filtering, sorting and export code works
   * unchanged. Employee-level sums (bank/merchant/account) are attributed to
   * the collector's dominant branch (the branch holding most of their billed
   * amount) since those uploads are recorded per employee, not per branch.
   */
  private rebuildAsBranches(scopedCollectors: any[]): void {
    const branchBillMap = new Map<string, CustomerBills[]>();
    const empBranchAmounts = new Map<number, Map<string, number>>();

    for (const c of scopedCollectors) {
      const bills: CustomerBills[] = c.customerBills || [];
      for (const b of bills) {
        const branch = (b.branchName || '').trim() || 'Unassigned';
        if (!branchBillMap.has(branch)) {
          branchBillMap.set(branch, []);
        }
        branchBillMap.get(branch)!.push(b);

        let perEmp = empBranchAmounts.get(c.employeeId);
        if (!perEmp) {
          perEmp = new Map<string, number>();
          empBranchAmounts.set(c.employeeId, perEmp);
        }
        perEmp.set(branch, (perEmp.get(branch) || 0) + (b.billAmount || 0));
      }
    }

    const branchKeys = Array.from(branchBillMap.keys()).sort();
    const branchIndex = new Map<string, number>();
    branchKeys.forEach((k, i) => branchIndex.set(k, i + 1));

    const employeeMap = new Map<number, CustomerBills[]>();
    const allBills: CustomerBills[] = [];
    const branchesAsEmployees: Employee[] = [];
    for (const key of branchKeys) {
      const idx = branchIndex.get(key)!;
      const bills = branchBillMap.get(key)!;
      employeeMap.set(idx, bills);
      allBills.push(...bills);
      branchesAsEmployees.push({
        employeeId: idx,
        employeeName: key,
        roles: [{ roleName: 'collector' }]
      } as Employee);
    }

    // Dominant branch per collector (by billed amount) for employee-level sums
    const dominantBranchIdx = new Map<number, number>();
    for (const c of scopedCollectors) {
      const perEmp = empBranchAmounts.get(c.employeeId);
      if (!perEmp) {
        continue;
      }
      let best: string | null = null;
      let bestAmt = -Infinity;
      for (const [br, amt] of perEmp) {
        if (amt > bestAmt) {
          bestAmt = amt;
          best = br;
        }
      }
      if (best != null) {
        dominantBranchIdx.set(c.employeeId, branchIndex.get(best)!);
      }
    }

    // Bank/merchant/account sums merged per dominant branch
    const bankByBranch = new Map<number, number>();
    if (this.bankAmountsCache) {
      for (const bank of this.bankAmountsCache) {
        const bIdx = dominantBranchIdx.get(bank.employeeId);
        if (bIdx != null) {
          bankByBranch.set(bIdx, (bankByBranch.get(bIdx) || 0) + (bank.bankAmount || 0));
        }
      }
    }
    this.bankAmountOverride = bankByBranch;

    const merchantByBranch: any = {};
    for (const [empKey, sums] of Object.entries(this.rawMerchantSums || {})) {
      const bIdx = dominantBranchIdx.get(Number(empKey));
      if (bIdx == null) {
        continue;
      }
      const key = String(bIdx);
      const cur = merchantByBranch[key] || { totalDebit: 0, totalCredit: 0 };
      cur.totalDebit += (sums as any)['totalDebit'] || 0;
      cur.totalCredit += (sums as any)['totalCredit'] || 0;
      merchantByBranch[key] = cur;
    }

    const accountByBranch: any = {};
    for (const [empKey, sums] of Object.entries(this.rawAccountSums || {})) {
      const bIdx = dominantBranchIdx.get(Number(empKey));
      if (bIdx == null) {
        continue;
      }
      const key = String(bIdx);
      const cur = accountByBranch[key] || { totalSentAmount: 0, totalReceivedAmount: 0 };
      cur.totalSentAmount += (sums as any)['totalSentAmount'] || 0;
      cur.totalReceivedAmount += (sums as any)['totalReceivedAmount'] || 0;
      accountByBranch[key] = cur;
    }

    this.employeesCache = branchesAsEmployees;
    this.employeesLoaded = true;
    this.processViewBillInfoData(employeeMap, allBills, branchesAsEmployees, merchantByBranch, accountByBranch);
  }
  

  private initializeAvailableCycles(): void {
    const today = new Date();
    const cycles: { label: string; startDate: string; endDate: string }[] = [];
    const seenLabels = new Set<string>(); // Track seen labels to prevent duplicates
    
    // Generate last 12 cycles
    for (let i = 0; i < 12; i++) {
      const cycleDate = new Date(today.getFullYear(), today.getMonth() - i, 15); // Use middle of month as reference
      let cycleStart = new Date(cycleDate.getFullYear(), cycleDate.getMonth(), 25);
      let cycleEnd = new Date(cycleDate.getFullYear(), cycleDate.getMonth() + 1, 24);
      
      // Adjust for previous month if start date is after today
      if (cycleStart > today) {
        cycleStart.setMonth(cycleStart.getMonth() - 1);
        cycleEnd.setMonth(cycleEnd.getMonth() - 1);
      }
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const label = `${monthNames[cycleStart.getMonth()]} ${cycleStart.getFullYear()} Cycle`;
      
      // Skip if this label already exists
      if (seenLabels.has(label)) {
        continue;
      }
      seenLabels.add(label);
      
      cycles.push({
        label,
        startDate: this.formatDate(cycleStart),
        endDate: this.formatDate(cycleEnd)
      });
    }
    
    this.availableCycles = cycles;
    
    // Set default to current cycle (first item)
    if (cycles.length > 0) {
      this.selectedCycleControl.setValue(cycles[0].label);
      this.currentCycleStart = cycles[0].startDate;
      this.currentCycleEnd = cycles[0].endDate;
    }
  }

  onCycleChange(selectedLabel: string): void {
    const selectedCycle = this.availableCycles.find(cycle => cycle.label === selectedLabel);
    if (selectedCycle) {
      this.selectedCycleControl.setValue(selectedLabel);
      this.currentCycleStart = selectedCycle.startDate;
      this.currentCycleEnd = selectedCycle.endDate;
      this.loadAllDataParallel();
    }
  }

  private loadAllDataParallel(): void {
    this.loading = true;

    // Single backend call: per-collector bills + bank/merchant/account sums in one response
    this.apiService.getBillingDashboardData(this.currentCycleStart, this.currentCycleEnd).subscribe({
      next: (data: any) => {
        this.rawCollectors = data?.collectors || [];
        this.rawMerchantSums = data?.merchantSums || {};
        this.rawAccountSums = data?.accountSums || {};

        this.bankAmountsCache = Array.isArray(data?.bankAmounts) ? data.bankAmounts : (data?.bankAmounts?.bankAmounts || []);
        this.bankAmountsLoaded = true;

        this.rebuildDashboard();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading dashboard data:', error);
        this.loading = false;
        this.snackBar.open('Error loading billing data', 'Close', { duration: 3000 });
      }
    });
  }

  private processViewBillInfoData(
    employeeMap: Map<number, CustomerBills[]>,
    allBills: CustomerBills[],
    collectorEmployees: Employee[],
    merchantSums: any,
    accountSums: any
  ): void {
    // Reset summary
    this.billingSummary = {
      totalCustomers: 0,
      totalBilledCustomers: 0,
      totalBillAmount: 0,
      totalPaid: 0,
      totalRh: 0,
      totalRc: 0,
      totalBankAmount: 0,
      totalFarqi: 0,
      totalBadDebt: 0,
      totalDiscount: 0,
      totalVat: 0,
      totalWadarta: 0,
      totalMerchantDebit: 0,
      totalMerchantCredit: 0,
      totalAccountSent: 0,
      totalAccountReceived: 0,
      totalUnitsConsumed: 0
    };

    // Create bank amount map (branch mode supplies a branch-indexed override)
    const bankAmountMap = new Map<number, number>();
    if (this.bankAmountOverride) {
      this.bankAmountOverride.forEach((v, k) => bankAmountMap.set(k, v));
    } else if (this.bankAmountsLoaded && this.bankAmountsCache.length > 0) {
      this.bankAmountsCache.forEach((bank: BankAmount) => {
        const currentAmount = bankAmountMap.get(bank.employeeId) || 0;
        bankAmountMap.set(bank.employeeId, currentAmount + (bank.bankAmount || 0));
      });
    }

    // Process each employee's bills from fixed SP
    const employeeSummaries: EmployeeBillingSummary[] = collectorEmployees.map(emp => {
      const bills = employeeMap.get(emp.employeeId) || [];

      const totalBillAmount = bills.reduce((sum, b) => sum + (b.billAmount || 0), 0);
      const totalPaid = bills.reduce((sum, b) => sum + (b.amountPaid || 0), 0);
      const totalRh = bills.reduce((sum, b) => sum + (b.prevMonthOutstandingBal || 0), 0);
      const totalWadarta = bills.reduce((sum, b) => sum + (b.wadarta || 0), 0);
      const totalBadDebt = bills.reduce((sum, b) => sum + (b.bad_debt || 0), 0);
      const totalDiscount = bills.reduce((sum, b) => sum + (b.discount || 0), 0);
      const totalVat = bills.reduce((sum, b) => sum + (b.vat || 0), 0);
      // RC computed in UI: wadarta - paid - bad debt - discount - vat (not from API totalOutstandingBal)
      const totalRc = totalWadarta - totalPaid - totalBadDebt - totalDiscount - totalVat;
      const totalUnitsConsumed = bills.reduce((sum, b) => sum + (b.unitsConsumed || 0), 0);
      const totalBilledCustomers = bills.filter(b => b.billGeneratedFlag === 'Y').length;

      const bankAmount = bankAmountMap.get(emp.employeeId) || 0;

      // Accumulate grand totals
      this.billingSummary.totalCustomers += bills.length;
      this.billingSummary.totalBilledCustomers += totalBilledCustomers;
      this.billingSummary.totalBillAmount += totalBillAmount;
      this.billingSummary.totalPaid += totalPaid;
      this.billingSummary.totalRh += totalRh;
      this.billingSummary.totalRc += totalRc;
      this.billingSummary.totalWadarta += totalWadarta;
      this.billingSummary.totalBadDebt += totalBadDebt;
      this.billingSummary.totalDiscount += totalDiscount;
      this.billingSummary.totalVat += totalVat;
      this.billingSummary.totalUnitsConsumed += totalUnitsConsumed;
      this.billingSummary.totalBankAmount += bankAmount;

      return {
        employeeId: emp.employeeId,
        employeeName: emp.employeeName || '',
        totalCustomers: bills.length,
        totalBilledCustomers,
        totalBillAmount,
        totalPaid,
        totalRh,
        totalRc,
        totalBadDebt,
        totalDiscount,
        totalVat,
        totalBankAmount: bankAmount,
        totalFarqi: 0,
        totalWadarta,
        totalMerchantDebit: 0,
        totalMerchantCredit: 0,
        totalAccountSent: 0,
        totalAccountReceived: 0,
        totalUnitsConsumed
      };
    });

    this.employeeSummaries = employeeSummaries;
    this.filteredEmployeeSummaries = employeeSummaries;

    // Apply merchant and account sums
    this.employeeSummaries.forEach(employee => {
      const empKey = String(employee.employeeId);
      const mSums = merchantSums[empKey];
      employee.totalMerchantDebit = mSums ? (mSums['totalDebit'] || 0) : 0;
      employee.totalMerchantCredit = mSums ? (mSums['totalCredit'] || 0) : 0;
      this.billingSummary.totalMerchantDebit += employee.totalMerchantDebit;
      this.billingSummary.totalMerchantCredit += employee.totalMerchantCredit;

      const aSums = accountSums[empKey];
      employee.totalAccountSent = aSums ? (aSums['totalSentAmount'] || 0) : 0;
      employee.totalAccountReceived = aSums ? (aSums['totalReceivedAmount'] || 0) : 0;
      this.billingSummary.totalAccountSent += employee.totalAccountSent;
      this.billingSummary.totalAccountReceived += employee.totalAccountReceived;

      // Calculate FARQI: (Account received + Merchant Credit + Bank Amount) - PAID
      employee.totalFarqi = (employee.totalAccountReceived + employee.totalMerchantCredit + employee.totalBankAmount) - employee.totalPaid;
      this.billingSummary.totalFarqi += employee.totalFarqi;
    });

    // Cache per-row bills for drill-down (employeeId or branch index -> bills)
    this.billsByRowKey = employeeMap;
    this.selectedDrillSummary = null;
    this.drillBills = [];

    // Load employee names
    this.loadEmployeeNames();
  }

  /** Open customer-level drill-down for a summary row (employee or branch). */
  drillDown(emp: EmployeeBillingSummary): void {
    this.selectedDrillSummary = emp;
    this.drillBills = this.billsByRowKey.get(emp.employeeId) || [];
    // Let the panel render, then scroll it into view
    setTimeout(() => {
      document.getElementById('drill-down-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  closeDrill(): void {
    this.selectedDrillSummary = null;
    this.drillBills = [];
  }

  toggleCycleSelector(): void {
    this.showCycleSelector = !this.showCycleSelector;
  }

  toggleDanGuudFilter(): void {
    this.excludeDanGuud = !this.excludeDanGuud;
    this.loadAllDataParallel();
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private loadEmployeeNames(): void {
    // Use cached employees — no extra API call
    const employees: Employee[] = Array.isArray(this.employeesCache)
      ? this.employeesCache
      : (this.employeesCache as any)?.employees || [];
    const employeeMap = new Map(employees.map((emp: Employee) => [emp.employeeId, emp.employeeName]));

    this.employeeSummaries.forEach(summary => {
      const name = employeeMap.get(summary.employeeId);
      summary.employeeName = name || `Employee ${summary.employeeId}`;
    });

    this.employeeSummaries.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
    this.filteredEmployeeSummaries = [...this.employeeSummaries];
    this.filterEmployees();
  }

  filterEmployees(): void {
    let filtered = this.employeeSummaries;

    // Employee multi-select filter (empty selection = show all)
    if (this.selectedEmployeeIds.size > 0) {
      filtered = filtered.filter(emp => this.selectedEmployeeIds.has(emp.employeeId));
    }

    if (this.searchTerm && this.searchTerm.trim() !== '') {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(emp =>
        emp.employeeName.toLowerCase().includes(searchLower) ||
        emp.employeeId.toString().includes(searchLower)
      );
    }

    // Top-N: keep the N best rows by the chosen metric, then let applySort
    // control the display ordering.
    if (this.topN > 0) {
      const metric = this.topNMetric;
      filtered = [...filtered]
        .sort((a, b) => ((b[metric] as number) || 0) - ((a[metric] as number) || 0))
        .slice(0, this.topN);
    }

    this.filteredEmployeeSummaries = [...filtered];
    this.applySort();
  }

  onTopNChange(): void {
    this.filterEmployees();
  }

  toggleEmployeeFilter(): void {
    this.showEmployeeFilter = !this.showEmployeeFilter;
  }

  isEmployeeSelected(employeeId: number): boolean {
    return this.selectedEmployeeIds.has(employeeId);
  }

  toggleEmployeeSelection(employeeId: number): void {
    if (this.selectedEmployeeIds.has(employeeId)) {
      this.selectedEmployeeIds.delete(employeeId);
    } else {
      this.selectedEmployeeIds.add(employeeId);
    }
    this.filterEmployees();
  }

  get isAllEmployeesSelected(): boolean {
    return this.employeeSummaries.length > 0 &&
      this.selectedEmployeeIds.size === this.employeeSummaries.length;
  }

  toggleSelectAllEmployees(): void {
    if (this.isAllEmployeesSelected) {
      this.selectedEmployeeIds.clear();
    } else {
      this.employeeSummaries.forEach(emp => this.selectedEmployeeIds.add(emp.employeeId));
    }
    this.filterEmployees();
  }

  get employeeFilterLabel(): string {
    if (this.selectedEmployeeIds.size === 0 ||
        this.selectedEmployeeIds.size === this.employeeSummaries.length) {
      return this.groupByBranch ? 'All Branches' : 'All Employees';
    }
    return this.selectedEmployeeIds.size + ' selected';
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  private applySort(): void {
    if (!this.sortColumn) {
      return;
    }
    const column = this.sortColumn as keyof EmployeeBillingSummary;
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredEmployeeSummaries = [...this.filteredEmployeeSummaries].sort((a, b) => {
      const aVal = a[column];
      const bVal = b[column];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      return ((aVal as number || 0) - (bVal as number || 0)) * direction;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  refreshDashboard(): void {
    this.loadAllDataParallel();
  }

  // Add test mode for debugging
  loadTestData(): void {
    console.log('Loading test data...');
    this.employeesCache = [
      { employeeId: 1, employeeName: 'John Doe', roles: [{ roleName: 'collector' }] },
      { employeeId: 2, employeeName: 'Jane Smith', roles: [{ roleName: 'collector' }] }
    ];
    this.employeesLoaded = true;

    const mockBills: CustomerBills[] = [
      {
        customerId: 1,
        customerName: 'Test Customer 1',
        customerPhone: 1234567890,
        customerPhone2: 0,
        customerPhone3: 0,
        customerAddress: '123 Test St',
        billDate: '2024-01-15',
        previousReading: 100,
        currentReading: 200,
        unitsConsumed: 100,
        billAmount: 1000,
        amountPaid: 800,
        prevMonthOutstandingBal: 100,
        totalOutstandingBal: 80,
        finalDueAmount: 200,
        wadarta: 50,
        bad_debt: 0,
        discount: 50,
        billGeneratedFlag: 'Y',
        bill_remarks: '',
        billPaidDate: '2024-01-20',
        employeeId: 1,
        employeeName: 'John Doe'
      } as CustomerBills,
      {
        customerId: 2,
        customerName: 'Test Customer 2',
        customerPhone: 9876543210,
        customerPhone2: 0,
        customerPhone3: 0,
        customerAddress: '456 Test Ave',
        billDate: '2024-01-15',
        previousReading: 200,
        currentReading: 350,
        unitsConsumed: 150,
        billAmount: 1500,
        amountPaid: 1200,
        prevMonthOutstandingBal: 150,
        totalOutstandingBal: 120,
        finalDueAmount: 300,
        wadarta: 75,
        bad_debt: 0,
        discount: 75,
        billGeneratedFlag: 'Y',
        bill_remarks: '',
        billPaidDate: '2024-01-20',
        employeeId: 2,
        employeeName: 'Jane Smith'
      } as CustomerBills
    ];

    const mockEmployees: any[] = [
      { employeeId: 1, employeeName: 'John Doe', roles: [{ roleName: 'collector' }] },
      { employeeId: 2, employeeName: 'Jane Smith', roles: [{ roleName: 'collector' }] }
    ];

    const employeeMap = new Map<number, CustomerBills[]>();
    employeeMap.set(1, [mockBills[0]]);
    employeeMap.set(2, [mockBills[1]]);

    this.processViewBillInfoData(employeeMap, mockBills, mockEmployees as any, {}, {});
    this.loading = false;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }

  get visibleColumns(): ColumnConfig[] {
    return this.allColumns.filter(c => c.visible);
  }

  isCountColumn(key: keyof EmployeeBillingSummary): boolean {
    return key === 'employeeId' || key === 'employeeName' || key === 'totalCustomers' || key === 'totalBilledCustomers' || key === 'totalUnitsConsumed';
  }

  isLabelColumn(key: keyof EmployeeBillingSummary): boolean {
    return key === 'employeeId' || key === 'employeeName';
  }

  getEmployeeDisplayValue(emp: EmployeeBillingSummary, key: keyof EmployeeBillingSummary): string {
    if (this.isCountColumn(key)) {
      return (emp[key] as any)?.toString() || '0';
    }
    return this.formatCurrency(emp[key] as number);
  }

  getSummaryDisplayValue(key: keyof EmployeeBillingSummary): string {
    if (this.isCountColumn(key)) {
      return ((this.billingSummary as any)[key] || 0).toString();
    }
    return this.formatCurrency((this.billingSummary as any)[key]);
  }

  toggleColumnSelector(): void {
    this.showColumnSelector = !this.showColumnSelector;
  }

  isColumnVisible(key: keyof EmployeeBillingSummary): boolean {
    const col = this.allColumns.find(c => c.key === key);
    return col ? col.visible : false;
  }

  toggleColumnVisibility(key: keyof EmployeeBillingSummary): void {
    const col = this.allColumns.find(c => c.key === key);
    if (col) {
      col.visible = !col.visible;
    }
  }

  moveColumnUp(index: number): void {
    if (index > 0) {
      const temp = this.allColumns[index];
      this.allColumns[index] = this.allColumns[index - 1];
      this.allColumns[index - 1] = temp;
    }
  }

  moveColumnDown(index: number): void {
    if (index < this.allColumns.length - 1) {
      const temp = this.allColumns[index];
      this.allColumns[index] = this.allColumns[index + 1];
      this.allColumns[index + 1] = temp;
    }
  }

  resetColumns(): void {
    this.allColumns.forEach(col => col.visible = true);
  }

  get visibleSummaryFields(): SummaryFieldConfig[] {
    return this.summaryFields.filter(f => f.visible);
  }

  toggleSummarySelector(): void {
    this.showSummarySelector = !this.showSummarySelector;
  }

  moveSummaryFieldUp(index: number): void {
    if (index > 0) {
      const temp = this.summaryFields[index];
      this.summaryFields[index] = this.summaryFields[index - 1];
      this.summaryFields[index - 1] = temp;
    }
  }

  moveSummaryFieldDown(index: number): void {
    if (index < this.summaryFields.length - 1) {
      const temp = this.summaryFields[index];
      this.summaryFields[index] = this.summaryFields[index + 1];
      this.summaryFields[index + 1] = temp;
    }
  }

  resetSummaryFields(): void {
    this.summaryFields.forEach(f => f.visible = true);
  }

  exportToCSV(): void {
    const csvData = this.generateCSV();
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `billing-${this.groupByBranch ? 'branch' : 'employee'}-dashboard-${this.currentCycleStart}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    this.snackBar.open('Dashboard exported successfully', 'Close', {
      duration: 3000
    });
  }

  exportToPDF(): void {
    exportBillingDashboardToPDF(
      this.billingSummary,
      this.filteredEmployeeSummaries,
      this.currentCycleStart,
      this.currentCycleEnd,
      this.excludeDanGuud,
      this.visibleColumns,
      this.visibleSummaryFields,
      this.groupByBranch ? 'Branch' : 'Employee'
    );
  }

  private generateCSV(): string {
    const cols = this.visibleColumns;
    const formatValue = (key: keyof EmployeeBillingSummary, val: any): string => {
      if (key === 'employeeName') return `"${(val || '').toString().replace(/"/g, '""')}"`;
      if (this.isCountColumn(key)) return (val || 0).toString();
      return this.formatCurrency(val).replace(/,/g, '');
    };

    let csv = 'Billing Dashboard Report\n';
    csv += `Billing Cycle: ${this.currentCycleStart} to ${this.currentCycleEnd}\n\n`;
    csv += 'Overall Summary\n';
    csv += 'Metric,Amount\n';

    const summaryRows = [
      { label: 'Total Customers', value: this.billingSummary.totalCustomers },
      { label: 'Billed Customers', value: this.billingSummary.totalBilledCustomers },
      { label: this.groupByBranch ? 'Total Branches' : 'Total Employees', value: this.employeeSummaries.length },
      { label: 'Total Units Consumed', value: this.billingSummary.totalUnitsConsumed },
      { label: 'Grand Total Bill', value: this.formatCurrency(this.billingSummary.totalBillAmount) },
      { label: 'Total Paid', value: this.formatCurrency(this.billingSummary.totalPaid) },
      { label: 'Total RH', value: this.formatCurrency(this.billingSummary.totalRh) },
      { label: 'Total RC', value: this.formatCurrency(this.billingSummary.totalRc) },
      { label: 'Total Daan Guud', value: this.formatCurrency(this.billingSummary.totalBadDebt) },
      { label: 'Total Discount', value: this.formatCurrency(this.billingSummary.totalDiscount) },
      { label: 'Total VAT', value: this.formatCurrency(this.billingSummary.totalVat) },
      { label: 'Total Wadarta', value: this.formatCurrency(this.billingSummary.totalWadarta) },
      { label: 'Total Bank Amount', value: this.formatCurrency(this.billingSummary.totalBankAmount) },
      { label: 'Total Farqi', value: this.formatCurrency(this.billingSummary.totalFarqi) },
      { label: 'Merchant Debit', value: this.formatCurrency(this.billingSummary.totalMerchantDebit) },
      { label: 'Merchant Credit', value: this.formatCurrency(this.billingSummary.totalMerchantCredit) },
      { label: 'Account Sent', value: this.formatCurrency(this.billingSummary.totalAccountSent) },
      { label: 'Account Received', value: this.formatCurrency(this.billingSummary.totalAccountReceived) }
    ];

    summaryRows.forEach(row => {
      csv += `"${row.label}","${row.value}"\n`;
    });

    csv += this.groupByBranch ? '\nBranch Summary\n' : '\nEmployee Summary\n';
    csv += cols.map(c => `"${c.label}"`).join(',') + '\n';

    this.employeeSummaries.forEach(emp => {
      const values = cols.map(c => formatValue(c.key, emp[c.key]));
      csv += values.join(',') + '\n';
    });

    // Total row
    const totalValues = cols.map((c, index) => {
      if (index === 0 && this.isLabelColumn(c.key)) return '"Total"';
      if (this.isCountColumn(c.key)) return ((this.billingSummary as any)[c.key] || 0).toString();
      return this.formatCurrency((this.billingSummary as any)[c.key]).replace(/,/g, '');
    });
    csv += totalValues.join(',') + '\n';

    return csv;
  }
}
